import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CUSTOM_PRICING, formatTierMonthlyPriceDisplay } from "../data/pricing";

import usa from "../../src/assets/images/usa.png";
import nl from "../../src/assets/images/nl.png";
import priceBG from "../../src/assets/images/price-bg.png";
import buyBG from "../../src/assets/images/buy-bg.png";
import PricingPlansBadge from "./PricingPlansBadge";

const PricingSection = ({ user, selectedLocation, setSelectedLocation, showBadge = true, descriptionNoWrap = false }) => {
  const [animKey, setAnimKey] = useState(0);
  const trackRef = useRef(null);
  const dragStartXRef = useRef(0);
  const dragStartLeftRef = useRef(0);
  const isDraggingRef = useRef(false);
  const suppressClickRef = useRef(false);
  const [dragLeft, setDragLeft] = useState(null); // null => snap to selectedLocation

  const MAX_LEFT = 35; // px (track width is 78px => 78-35-8 = 35)
  const TAP_THRESHOLD = 8; // px — tap thumb to toggle side

  const handleLocationChange = (loc) => {
    if (loc === selectedLocation) return;
    setSelectedLocation(loc);
    setAnimKey((k) => k + 1);
  };

  useEffect(() => {
    if (!isDraggingRef.current) setDragLeft(null);
  }, [selectedLocation]);

  return (
    <section
      id="pricing"
      className="relative z-10 mx-auto max-w-7xl px-4 pb-6 sm:px-6 lg:px-8 flex flex-col gap-4"
    >
      {/* Title + Location switcher */}
      <div className="reveal reveal-up delay-2 mb-4 flex w-full flex-col gap-6 items-center text-center sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:text-left">
        <div className={`flex min-w-0 flex-col items-center sm:items-start ${showBadge ? "gap-5" : "gap-0"} ${descriptionNoWrap ? "lg:flex-1" : ""}`}>
          {showBadge ? <PricingPlansBadge /> : null}
          <h2 className="hidden md:block text-2xl text-white sm:text-4xl">
            Deploy on AMD Ryzen 9 9950X
          </h2>
          <h2 className="block md:hidden text-4xl text-white">
            Deploy on <br /> AMD Ryzen 9 9950X
          </h2>          
          <p
            className={`text-sm text-[#9B9DA7] mx-auto sm:mx-0 ${
              descriptionNoWrap
                ? "max-w-lg lg:max-w-none lg:whitespace-nowrap"
                : "max-w-lg"
            }`}
          >
            Instant provisioning in strategically located data centers with low-latency routing and high-speed backbone connectivity.
          </p>
        </div>

        <div className="flex gap-4 items-center justify-center shrink-0 sm:justify-end">
        <div
          onClick={() => handleLocationChange("us")}
          className="flex items-center gap-2 cursor-pointer select-none"
        >
          <img src={usa} alt="usa" />
          <p className={selectedLocation === "us" ? "text-white text-xl" : "text-white/60 text-xl"}>
            USA
          </p>
        </div>

        {/* Draggable switch */}
        <div
          ref={trackRef}
          className="relative w-[78px] h-[43px] rounded-full border border-[#0B3BF0] bg-transparent p-1 select-none"
          onClick={(e) => {
            if (isDraggingRef.current || suppressClickRef.current) {
              suppressClickRef.current = false;
              return;
            }
            if (e.target.closest("[data-location-thumb]")) {
              handleLocationChange(selectedLocation === "us" ? "nl" : "us");
              return;
            }
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            handleLocationChange(x > rect.width / 2 ? "nl" : "us");
          }}
          role="switch"
          aria-checked={selectedLocation === "nl"}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleLocationChange(selectedLocation === "us" ? "nl" : "us");
            }
            if (e.key === "ArrowLeft") {
              e.preventDefault();
              handleLocationChange("us");
            }
            if (e.key === "ArrowRight") {
              e.preventDefault();
              handleLocationChange("nl");
            }
          }}
        >
          <div
            data-location-thumb
            className="absolute top-1 left-1 z-10 h-[35px] w-[35px] cursor-pointer rounded-full bg-[#0B3BF0] transition-transform touch-none"
            style={{
              transform: `translateX(${dragLeft ?? (selectedLocation === "nl" ? MAX_LEFT : 0)}px)`,
              transitionDuration: isDraggingRef.current ? "0ms" : "200ms",
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
              isDraggingRef.current = true;
              dragStartXRef.current = e.clientX;
              dragStartLeftRef.current = dragLeft ?? (selectedLocation === "nl" ? MAX_LEFT : 0);
              e.currentTarget.setPointerCapture?.(e.pointerId);
            }}
            onPointerMove={(e) => {
              if (!isDraggingRef.current) return;
              const delta = e.clientX - dragStartXRef.current;
              const next = Math.min(
                MAX_LEFT,
                Math.max(0, dragStartLeftRef.current + delta)
              );
              setDragLeft(next);
            }}
            onPointerUp={(e) => {
              e.stopPropagation();
              if (!isDraggingRef.current) return;
              isDraggingRef.current = false;
              e.currentTarget.releasePointerCapture?.(e.pointerId);

              const moved = Math.abs(e.clientX - dragStartXRef.current);
              if (moved < TAP_THRESHOLD) {
                setDragLeft(null);
                return;
              }

              suppressClickRef.current = true;
              const current = dragLeft ?? (selectedLocation === "nl" ? MAX_LEFT : 0);
              const nextLoc = current >= MAX_LEFT / 2 ? "nl" : "us";
              setDragLeft(null);
              handleLocationChange(nextLoc);
            }}
            onPointerCancel={(e) => {
              isDraggingRef.current = false;
              e.currentTarget.releasePointerCapture?.(e.pointerId);
              setDragLeft(null);
            }}
          />
        </div>

        <div
          onClick={() => handleLocationChange("nl")}
          className="flex items-center gap-2 cursor-pointer select-none"
        >
          <img src={nl} alt="nl" />
          <p className={selectedLocation === "nl" ? "text-white text-xl" : "text-white/60 text-xl"}>
            NL
          </p>
        </div>
        </div>
      </div>

      {/* Cards */}
      <div
        key={animKey}
        className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
      >
        {CUSTOM_PRICING.tiers.map((tier, idx) => (
          <div
            key={`${tier.id}-${animKey}`}
            className="group  rounded-2xl border border-[#FFFFFF66]
            bg-gradient-to-b from-[#0A0B18] to-[#141522]
            p-4 bg-no-repeat bg-bottom bg-cover 
            shadow-[0_16px_30px_rgba(2,6,23,0.45)]
            transition-all duration-500 ease-out
            hover:-translate-y-3 hover:scale-[1.1]
            hover:border-blue-400/50
            hover:shadow-[0_25px_60px_rgba(59,130,246,0.35)]
            animate-cardIn"
            style={{
              backgroundImage: `url(${buyBG})`,
              animationDelay: `${idx * 90}ms`,
            }}
          >
            {/* Price box */}
            <div
              className="rounded-xl border border-blue-300/25 bg-[#03061433] px-3 py-3 bg-no-repeat bg-right-top"
              style={{ backgroundImage: `url(${priceBG})` }}
            >
              <p className="text-sm uppercase tracking-widest text-blue-100/75 sm:text-xs">{tier.name}</p>
              <div className="mt-1 flex items-end gap-1">
                <span key={animKey} className="price-animate text-5xl font-semibold tabular-nums leading-none text-white sm:text-4xl">
                  {formatTierMonthlyPriceDisplay(tier, selectedLocation)}
                </span>
                <span className="pb-0.5 text-base leading-none text-blue-100/70 sm:pb-1 sm:text-sm">/ monthly</span>
              </div>
            </div>

            {/* Specs */}
            <div className="mt-4 space-y-4 text-[0.95rem] leading-snug text-blue-100/85 sm:space-y-3.5 sm:text-sm sm:leading-normal">
              <div className="flex items-center gap-2.5 sm:gap-2">
                <div className="p-1 rounded-lg bg-[#0B3BF0]">
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10.5306 8.52098C10.5306 9.46674 10.5306 9.93874 10.2372 10.2328C9.94298 10.5261 9.4699 10.5261 8.52455 10.5261H5.8498C4.90445 10.5261 4.43137 10.5261 4.13715 10.2328C3.84376 9.93874 3.84375 9.46674 3.84375 8.52098V5.84745C3.84375 4.90252 3.84376 4.42962 4.13715 4.13636C4.43137 3.84228 4.90445 3.84229 5.8498 3.84229H8.52455C9.4699 3.84229 9.94298 3.84228 10.2372 4.13636C10.5306 4.42962 10.5306 4.90252 10.5306 5.84745" stroke="white" strokeLinecap="round"/>
                    <path d="M2.17188 7.18424C2.17188 4.82152 2.17187 3.64018 2.90659 2.90579C3.64047 2.17141 4.82321 2.17139 7.18701 2.17139C9.55081 2.17139 10.7335 2.17141 11.4674 2.90579C12.2021 3.64018 12.2021 4.82152 12.2021 7.18424C12.2021 9.5478 12.2021 10.7292 11.4674 11.4627C10.7335 12.1971 9.55081 12.1971 7.18701 12.1971C4.82321 12.1971 3.64047 12.1971 2.90659 11.4627C2.17187 10.7292 2.17188 9.5478 2.17188 7.18424Z" stroke="white"/>
                    <path d="M2.17171 7.18408H0.5" stroke="white" strokeLinecap="round"/>
                    <path d="M13.8739 7.18408H12.2021" stroke="white" strokeLinecap="round"/>
                    <path d="M2.17171 5.51318H0.5" stroke="white" strokeLinecap="round"/>
                    <path d="M13.8739 5.51318H12.2021" stroke="white" strokeLinecap="round"/>
                    <path d="M2.17171 8.85498H0.5" stroke="white" strokeLinecap="round"/>
                    <path d="M13.8739 8.85498H12.2021" stroke="white" strokeLinecap="round"/>
                    <path d="M7.18652 12.1968V13.8677" stroke="white" strokeLinecap="round"/>
                    <path d="M7.18652 0.5V2.17095" stroke="white" strokeLinecap="round"/>
                    <path d="M5.51562 12.1968V13.8677" stroke="white" strokeLinecap="round"/>
                    <path d="M5.51562 0.5V2.17095" stroke="white" strokeLinecap="round"/>
                    <path d="M8.8584 12.1968V13.8677" stroke="white" strokeLinecap="round"/>
                    <path d="M8.8584 0.5V2.17095" stroke="white" strokeLinecap="round"/>
                  </svg>
                </div>
                {tier.specs.cores} Cores
              </div>

              <div className="flex items-center gap-2.5 sm:gap-2">
                <div className="p-1 rounded-lg bg-[#0B3BF0]">
                  <svg width="14" height="9" viewBox="0 0 14 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M13.1271 0.0292702C13.0786 0.0100543 13.0268 0 12.9742 0L0.398695 0.0501265C0.292541 0.050962 0.191417 0.0919007 0.11619 0.165423C0.0417991 0.238944 0 0.338361 0 0.44196L0.0150499 6.87432C0.0158857 7.08987 0.194762 7.26529 0.415428 7.26696L1.43599 7.27196V7.96374C1.43599 8.18013 1.61404 8.35476 1.83471 8.35476H3.53065C3.53065 8.35476 3.9511 7.35386 5.10291 7.35386C6.25555 7.35386 6.68685 8.35476 6.68685 8.35476H11.6159C11.6685 8.35476 11.7204 8.34472 11.7688 8.32467C11.8173 8.30545 11.8616 8.27625 11.8984 8.24033C11.936 8.2044 11.9653 8.16092 11.9853 8.1133C12.0054 8.06651 12.0154 8.01554 12.0154 7.96374V7.43325H12.9733C13.026 7.43325 13.0778 7.42322 13.1263 7.40317C13.1748 7.38395 13.219 7.35554 13.2567 7.31877C13.2934 7.28285 13.3227 7.23942 13.3436 7.1918C13.3637 7.14501 13.3737 7.09404 13.3737 7.04224V0.389334C13.3737 0.337535 13.3637 0.287393 13.3436 0.239771C13.3235 0.192984 13.2943 0.149539 13.2567 0.113613C13.2199 0.0776878 13.1756 0.0484861 13.1271 0.0292702ZM1.04984 1.12874C1.07492 1.10451 1.10833 1.09029 1.14428 1.09029H4.07311C4.09067 1.09029 4.10822 1.09365 4.1241 1.10034C4.14082 1.10702 4.15503 1.11621 4.16757 1.12874C4.18011 1.14044 4.19014 1.15463 4.19683 1.1705C4.20352 1.18638 4.20685 1.20392 4.20685 1.22063L4.19767 5.77398C4.19767 5.77398 3.9235 5.78316 3.68778 5.93522C3.45207 6.08728 3.39191 6.29534 3.39191 6.29534H1.01056V1.22063C1.01056 1.18554 1.02477 1.15297 1.04984 1.12874ZM5.17813 1.12874C5.20321 1.10451 5.23664 1.09029 5.27175 1.09029H8.20143C8.22817 1.09029 8.25408 1.09781 8.27581 1.11201C8.29754 1.12622 8.31427 1.14711 8.3243 1.1705C8.33099 1.18638 8.33432 1.20308 8.33432 1.22063L8.32512 6.165C8.32512 6.20009 8.31091 6.23264 8.28583 6.2577C8.26076 6.28193 8.22648 6.29534 8.19138 6.29534H6.84732C6.84732 6.29534 6.52553 6.0054 6.1402 5.87423C5.75404 5.74306 5.13885 5.71468 5.13885 5.71468V1.21981C5.13885 1.18556 5.15306 1.15297 5.17813 1.12874ZM9.30642 1.12874C9.33066 1.10451 9.36493 1.09029 9.40003 1.09029H12.3297C12.3565 1.09029 12.3815 1.09781 12.4041 1.11201C12.4258 1.12622 12.4426 1.14711 12.4526 1.1705C12.4593 1.18638 12.4626 1.20308 12.4626 1.22063L12.4534 6.165C12.4534 6.20009 12.4392 6.23266 12.4141 6.25689C12.389 6.28112 12.3556 6.29534 12.3205 6.29534H9.26714V1.22063C9.26714 1.18554 9.28134 1.15297 9.30642 1.12874Z" fill="white"/>
                  </svg>

                </div>
                {tier.specs.ram} GB DDR5 RAM
              </div>

              <div className="flex items-center gap-2.5 sm:gap-2">
                <div className="p-1 rounded-lg bg-[#0B3BF0]">
                  <svg width="13" height="10" viewBox="0 0 13 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.3512 1.35178L10.7708 1.25321L10.77 1.24903L10.7683 1.24572L10.3512 1.35178ZM2.1866 1.35178L1.76951 1.24572L1.76783 1.24903L1.76701 1.25321L2.1866 1.35178ZM11.5942 6.08647C11.661 6.27445 11.9034 6.38223 12.1358 6.32792C12.3673 6.27362 12.5011 6.07724 12.4342 5.89009L11.5942 6.08647ZM0.103643 5.89009C0.0367744 6.07724 0.170507 6.27362 0.402039 6.32792C0.634407 6.38223 0.876813 6.27445 0.943682 6.08647L0.103643 5.89009ZM11.6025 3.57168C11.5357 3.3837 11.2924 3.27592 11.0609 3.33022C10.8285 3.38453 10.6948 3.58086 10.7625 3.768L11.6025 3.57168ZM10.1389 2.02938C10.2058 2.21737 10.449 2.32514 10.6806 2.27084C10.9129 2.21653 11.0458 2.02021 10.979 1.83306L10.1389 2.02938ZM6.26892 8.48341C6.02736 8.48341 5.83176 8.64135 5.83176 8.83685C5.83176 9.03235 6.02736 9.19023 6.26892 9.19023V8.48341ZM3.93606 9.19023C4.17762 9.19023 4.37404 9.03235 4.37404 8.83685C4.37404 8.64135 4.17762 8.48341 3.93606 8.48341V9.19023ZM3.64432 0.706819H8.89352V0H3.64432V0.706819ZM8.89352 0.706819C9.1426 0.706819 9.33232 0.754439 9.48528 0.850519C9.64243 0.94994 9.80543 1.12872 9.93415 1.4579L10.7683 1.24572C10.6045 0.826309 10.3613 0.506309 10.0227 0.293263C9.68088 0.0777103 9.28804 0 8.89352 0V0.706819ZM3.64432 0C3.25063 0 2.85695 0.0777103 2.51425 0.293263C2.17657 0.506309 1.93334 0.826309 1.76951 1.24572L2.60368 1.4579C2.73241 1.12872 2.89541 0.94994 3.05255 0.850519C3.20552 0.754439 3.39524 0.706819 3.64432 0.706819V0ZM1.76701 1.25321L0.103643 5.89009L0.943682 6.08647L2.60703 1.4504L1.76701 1.25321ZM2.47831 5.69713H10.0595V4.99031H2.47831V5.69713ZM2.47831 8.48341C2.05955 8.48341 1.77954 8.48257 1.56723 8.46419C1.36328 8.44664 1.27052 8.41577 1.20866 8.37984L0.70296 8.95714C0.935328 9.08998 1.19444 9.14263 1.47362 9.16769C1.7436 9.19109 2.07877 9.19023 2.47831 9.19023V8.48341ZM2.62863e-06 7.09068C2.62863e-06 7.43406 -0.000822412 7.71645 0.0267608 7.9437C0.0543441 8.1768 0.113687 8.38987 0.264141 8.58203L1.00386 8.20438C0.957048 8.14506 0.918612 8.05731 0.897716 7.87685C0.875148 7.69053 0.875141 7.44743 0.875141 7.09068H2.62863e-06ZM1.20866 8.37984C1.12926 8.33473 1.05902 8.27539 1.00386 8.20438L0.264141 8.58203C0.378653 8.72908 0.528266 8.85688 0.70296 8.95714L1.20866 8.37984ZM11.6627 7.09068C11.6627 7.44743 11.6627 7.69053 11.6401 7.87685C11.6192 8.05731 11.5808 8.14506 11.534 8.20438L12.2737 8.58203C12.4242 8.38987 12.4835 8.1768 12.5111 7.9437C12.5387 7.71645 12.5378 7.43406 12.5378 7.09068H11.6627ZM10.0595 9.19023C10.4591 9.19023 10.7942 9.19109 11.0642 9.16769C11.3434 9.14263 11.6025 9.08998 11.8349 8.95714L11.3292 8.37984C11.2673 8.41577 11.1746 8.44664 10.9706 8.46419C10.7583 8.48257 10.4783 8.48341 10.0595 8.48341V9.19023ZM11.534 8.20438C11.4788 8.27539 11.4094 8.33473 11.3292 8.37984L11.8349 8.95714C12.0096 8.85688 12.1592 8.72908 12.2737 8.58203L11.534 8.20438ZM10.0595 5.69713C10.4783 5.69713 10.7583 5.69793 10.9706 5.71631C11.1746 5.73385 11.2673 5.76478 11.3292 5.8007L11.8349 5.2234C11.6025 5.09056 11.3434 5.03795 11.0642 5.01372C10.7942 4.98949 10.4591 4.99031 10.0595 4.99031V5.69713ZM12.5378 7.09068C12.5378 6.74646 12.5387 6.4641 12.5111 6.23685C12.4835 6.00459 12.4242 5.79067 12.2737 5.59851L11.534 5.97617C11.5808 6.03549 11.6192 6.12322 11.6401 6.30452C11.6627 6.48999 11.6627 6.7331 11.6627 7.09068H12.5378ZM11.3292 5.8007C11.4094 5.84582 11.4788 5.90515 11.534 5.97617L12.2737 5.59851C12.1592 5.45147 12.0096 5.32366 11.8349 5.2234L11.3292 5.8007ZM2.47831 4.99031C2.07877 4.99031 1.7436 4.98949 1.47362 5.01372C1.19444 5.03795 0.935328 5.09056 0.70296 5.2234L1.20866 5.8007C1.27052 5.76478 1.36328 5.73385 1.56723 5.71631C1.77954 5.69793 2.05955 5.69713 2.47831 5.69713V4.99031ZM0.875141 7.09068C0.875141 6.7331 0.875148 6.48999 0.897716 6.30452C0.918612 6.12322 0.957048 6.03549 1.00386 5.97617L0.264141 5.59851C0.113687 5.79067 0.0543441 6.00459 0.0267608 6.23685C-0.000822412 6.4641 2.62863e-06 6.74646 2.62863e-06 7.09068H0.875141ZM0.70296 5.2234C0.528266 5.32366 0.378653 5.45147 0.264141 5.59851L1.00386 5.97617C1.05902 5.90515 1.12926 5.84582 1.20866 5.8007L0.70296 5.2234ZM10.7625 3.768L11.5942 6.08647L12.4342 5.89009L11.6025 3.57168L10.7625 3.768ZM9.93081 1.4504L10.1389 2.02938L10.979 1.83306L10.7708 1.25321L9.93081 1.4504ZM10.0595 8.48341H6.26892V9.19023H10.0595V8.48341ZM3.93606 8.48341H2.47831V9.19023H3.93606V8.48341Z" fill="white"/>
                  </svg>

                </div>
                {tier.specs.storage} GB NVMe
              </div>

              <div className="flex items-center gap-2.5 sm:gap-2">
                <div className="p-1 rounded-lg bg-[#0B3BF0]">
                  <svg width="15" height="15" viewBox="0 0 13 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 3.08627L0.917763 2.18226C2.33705 0.785347 4.26203 0 6.26892 0C8.27581 0 10.2008 0.785347 11.6201 2.18226L12.5378 3.08627L11.4295 4.17738L10.5117 3.27342C9.38665 2.16558 7.86039 1.54311 6.26892 1.54311C4.67745 1.54311 3.15119 2.16558 2.02612 3.27342L1.10834 4.17738L0 3.08627Z" fill="white"/>
                    <path d="M3.53674 6.6837L2.50781 5.57L3.35956 4.64682C4.13189 3.81134 5.17837 3.3418 6.26916 3.3418C7.35995 3.3418 8.40644 3.81134 9.17877 4.64682L10.0305 5.57L9.00159 6.6837L8.14984 5.76133C7.65083 5.22078 6.97463 4.91749 6.26916 4.91749C5.5637 4.91749 4.88749 5.22078 4.38849 5.76133L3.53674 6.6837Z" fill="white"/>
                    <path d="M6.26933 10.0255L4.17969 8.06796L5.22451 7.08879C5.50201 6.82979 5.87731 6.68359 6.26933 6.68359C6.66134 6.68359 7.03748 6.82979 7.31415 7.08879L8.35897 8.06796L6.26933 10.0255Z" fill="white"/>
                  </svg>
                </div>
                {tier.specs.bandwidth} Uplink
              </div>

              <div className="flex items-center gap-2.5 sm:gap-2">
                <div className="p-1 rounded-lg bg-[#0B3BF0]">
                  <svg width="15" height="15" viewBox="0 0 13 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.599609 5.61295C0.599609 4.95293 0.740029 4.30121 1.01335 3.69132C1.28584 3.08977 1.68621 2.52995 2.19107 2.07044C2.69509 1.60257 3.29441 1.23503 3.9539 0.984384C4.61255 0.733741 5.31885 0.600098 6.03267 0.600098C6.74649 0.600098 7.45279 0.733741 8.11144 0.984384C8.77093 1.23503 9.37025 1.60257 9.87427 2.07044C10.3791 2.52995 10.7795 3.08977 11.052 3.69132C11.3253 4.30121 11.4657 4.95293 11.4657 5.61295" stroke="white" strokeWidth="1.2" strokeLinejoin="round"/>
                    <path d="M4.7793 7.28377C4.7793 6.95794 4.82277 6.63208 4.90635 6.32295C4.99078 6.02218 5.11364 5.7465 5.26911 5.51257C5.42458 5.27863 5.60845 5.09481 5.81157 4.96949C6.01384 4.84417 6.23118 4.77734 6.45101 4.77734C6.67084 4.77734 6.88817 4.84417 7.09045 4.96949C7.29356 5.09481 7.47744 5.27863 7.63291 5.51257C7.78838 5.7465 7.91124 6.02218 7.99566 6.32295C8.07925 6.63208 8.12272 6.95794 8.12272 7.28377" stroke="white" strokeWidth="1.2" strokeLinejoin="round"/>
                    <path d="M7.28613 4.77737L8.12199 3.94189" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M11.4657 5.61279V6.44827C11.4657 6.90778 11.1615 7.28374 10.787 7.28374H1.27915C0.903851 7.28374 0.599609 6.90778 0.599609 6.44827V5.61279" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                {tier.specs.cpu}
              </div>
            </div>

            {/* CTA */}
            <Link
              to={user ? `/order?plan=${tier.id}&location=${selectedLocation}` : "/signup"}
              className="group mx-auto mt-4 block w-full rounded-xl bg-[#FFFFFF0F] px-4 py-2.5 text-center text-base font-medium text-white shadow-[0_2px_4px_rgba(0,0,0,0.25),inset_0_1px_1px_rgba(255,255,255,0.23),inset_0_-16px_28px_rgba(255,255,255,0.15)] relative overflow-hidden transition-all duration-300 ease-out hover:-translate-y-0.5 hover:bg-[#FFFFFF14] hover:shadow-[0_10px_22px_rgba(0,0,0,0.35),inset_0_1px_1px_rgba(255,255,255,0.28),inset_0_-16px_28px_rgba(255,255,255,0.18)] active:translate-y-0 active:scale-[0.99] sm:py-2.5 sm:text-sm"
            >
              <span className="pointer-events-none absolute inset-0 -translate-x-[120%] skew-x-[-20deg] bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-[120%]" />
              <span className="relative z-10">Buy Now</span>
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
};

export default PricingSection;